import Axios from "axios";
import { configureOidc } from "@/store/services/security.service";

interface PushPublicKeyResponse {
    publicKey: string;
}

interface PushSubscriptionPayload {
    endpoint: string;
    expirationTime: number | null;
    keys: {
        p256dh: string;
        auth: string;
    };
}

function isPushSupported(): boolean {
    return "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window;
}

async function getAuthorizationHeader(): Promise<{ Authorization: string }> {
    const oidc = await configureOidc();
    if (!oidc.accessToken) {
        throw new Error("You must be signed in to manage push notifications.");
    }

    return { Authorization: `Bearer ${oidc.accessToken}` };
}

function decodeApplicationServerKey(value: string): Uint8Array {
    const padding = "=".repeat((4 - (value.length % 4)) % 4);
    const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

function serializeSubscription(subscription: PushSubscription): PushSubscriptionPayload {
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
        throw new Error("The browser returned an incomplete push subscription.");
    }

    return {
        endpoint: json.endpoint,
        expirationTime: json.expirationTime ?? null,
        keys: {
            p256dh: json.keys.p256dh,
            auth: json.keys.auth,
        },
    };
}

async function saveSubscription(subscription: PushSubscription): Promise<void> {
    const headers = await getAuthorizationHeader();
    await Axios.post(
        "/api/push/subscription",
        serializeSubscription(subscription),
        { headers }
    );
}

export async function getDevicePushSubscription(): Promise<PushSubscription | null> {
    if (!isPushSupported()) {
        return null;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    return registration ? registration.pushManager.getSubscription() : null;
}

export async function getPushPublicKey(): Promise<string> {
    const headers = await getAuthorizationHeader();
    const { data } = await Axios.get<PushPublicKeyResponse>(
        "/api/push/public-key",
        { headers }
    );
    if (!data.publicKey) {
        throw new Error("Push notifications are not configured on the server.");
    }
    return data.publicKey;
}

export async function enablePushNotifications(publicKey: string): Promise<void> {
    if (!isPushSupported()) {
        throw new Error("Push notifications are not supported by this browser.");
    }
    if (!publicKey) {
        throw new Error("Push notifications are not configured on the server.");
    }

    const permissionPromise = Notification.requestPermission();
    const registrationPromise = navigator.serviceWorker.getRegistration();
    const permission = await permissionPromise;
    if (permission !== "granted") {
        throw new Error(
            permission === "denied"
                ? "Notifications are blocked. Allow them in your browser or device settings."
                : "Notification permission was not granted."
        );
    }

    const registration = await registrationPromise;
    if (!registration) {
        throw new Error("The app service worker is not installed. Use the production PWA before enabling push.");
    }
    const subscription = await registration.pushManager.getSubscription() ??
        await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: decodeApplicationServerKey(publicKey),
        });

    await saveSubscription(subscription);
}

export async function disablePushNotifications(): Promise<void> {
    const subscription = await getDevicePushSubscription();
    if (!subscription) {
        return;
    }

    const headers = await getAuthorizationHeader();
    await Axios.post(
        "/api/push/subscription/unsubscribe",
        { endpoint: subscription.endpoint },
        { headers }
    );

    const unsubscribed = await subscription.unsubscribe();
    if (!unsubscribed) {
        throw new Error("The browser could not unsubscribe this device.");
    }
}

export async function syncDevicePushSubscription(): Promise<PushSubscription | null> {
    const subscription = await getDevicePushSubscription();
    if (subscription && Notification.permission === "granted") {
        await saveSubscription(subscription);
    }
    return subscription;
}
